import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
 import birthDataService from'../../utils/birthDataService';
 import Button from'../ui/Button';
 import Input from'../ui/Input';
 import Icon from'../AppIcon';

const zodiacSigns = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'
]

const BirthDataSetup = () => {
  const [formData, setFormData] = useState({
    birth_date: '',
    birth_time: '',
    birth_city: '',
    birth_country: '',
    latitude: '',
    longitude: '',
    timezone: 'America/New_York',
    sun_sign: '',
    moon_sign: '',
    rising_sign: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isLocationSearch, setIsLocationSearch] = useState(false)
  
  const { user } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError(null)
  }

  const handleLocationSearch = async () => {
    if (!formData.birth_city || !formData.birth_country) {
      setError('Please enter both city and country')
      return
    }

    setIsLocationSearch(true)
    
    try {
      // Mock geocoding - in production, use a real geocoding service
      const mockCoordinates = {
        'New York': { lat: 40.7128, lng: -74.0060, timezone: 'America/New_York' },
        'Los Angeles': { lat: 34.0522, lng: -118.2437, timezone: 'America/Los_Angeles' },
        'Chicago': { lat: 41.8781, lng: -87.6298, timezone: 'America/Chicago' },
        'London': { lat: 51.5074, lng: -0.1278, timezone: 'Europe/London' },
        'Tokyo': { lat: 35.6762, lng: 139.6503, timezone: 'Asia/Tokyo' }
      }

      const coordinates = mockCoordinates[formData.birth_city] || mockCoordinates['New York']
      
      setFormData(prev => ({
        ...prev,
        latitude: coordinates.lat.toString(),
        longitude: coordinates.lng.toString(),
        timezone: coordinates.timezone
      }))
    } catch (error) {
      setError('Failed to find location. Please enter coordinates manually.')
    } finally {
      setIsLocationSearch(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!user?.id) {
      setError('User not authenticated')
      return
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      const result = await birthDataService.saveBirthData(user.id, {
        birth_date: formData.birth_date,
        birth_time: formData.birth_time,
        birth_city: formData.birth_city,
        birth_country: formData.birth_country,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone,
        sun_sign: formData.sun_sign,
        moon_sign: formData.moon_sign,
        rising_sign: formData.rising_sign
      })
      
      if (result?.success) {
        navigate('/dashboard')
      } else {
        setError(result?.error || 'Failed to save birth data')
      }
    } catch (error) {
      setError('Something went wrong. Please try again.')
      console.log('Birth data save error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6 animate-cosmic-glow">
            <Icon name="Globe" size={32} color="white" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-text-primary">
            Birth Data Setup
          </h2>
          <p className="mt-2 text-sm text-text-secondary">
            Enter your birth information to generate your cosmic musical profile
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Birth Date & Time */}
            <div>
              <label htmlFor="birth_date" className="block text-sm font-medium text-text-primary mb-2">
                Birth Date
              </label>
              <Input
                id="birth_date"
                name="birth_date"
                type="date"
                required
                value={formData.birth_date}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="birth_time" className="block text-sm font-medium text-text-primary mb-2">
                Birth Time
              </label>
              <Input
                id="birth_time"
                name="birth_time"
                type="time"
                required
                value={formData.birth_time}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            {/* Location */}
            <div>
              <label htmlFor="birth_city" className="block text-sm font-medium text-text-primary mb-2">
                Birth City
              </label>
              <Input
                id="birth_city"
                name="birth_city"
                type="text"
                required
                placeholder="e.g., New York"
                value={formData.birth_city}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="birth_country" className="block text-sm font-medium text-text-primary mb-2">
                Birth Country
              </label>
              <Input
                id="birth_country"
                name="birth_country"
                type="text"
                required
                placeholder="e.g., United States"
                value={formData.birth_country}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            {/* Coordinates */}
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-text-primary mb-2">
                Latitude
              </label>
              <Input
                id="latitude"
                name="latitude"
                type="number"
                step="any"
                required
                placeholder="40.7128"
                value={formData.latitude}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-text-primary mb-2">
                Longitude
              </label>
              <Input
                id="longitude"
                name="longitude"
                type="number"
                step="any"
                required
                placeholder="-74.0060"
                value={formData.longitude}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            {/* Zodiac Signs */}
            <div>
              <label htmlFor="sun_sign" className="block text-sm font-medium text-text-primary mb-2">
                Sun Sign
              </label>
              <select
                id="sun_sign"
                name="sun_sign"
                required
                value={formData.sun_sign}
                onChange={handleChange}
                className="w-full px-3 py-2 glass border border-white/20 rounded-xl bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select Sun Sign</option>
                {zodiacSigns.map(sign => (
                  <option key={sign} value={sign} className="bg-surface text-text-primary">
                    {sign.charAt(0).toUpperCase() + sign.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="moon_sign" className="block text-sm font-medium text-text-primary mb-2">
                Moon Sign
              </label>
              <select
                id="moon_sign"
                name="moon_sign"
                required
                value={formData.moon_sign}
                onChange={handleChange}
                className="w-full px-3 py-2 glass border border-white/20 rounded-xl bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select Moon Sign</option>
                {zodiacSigns.map(sign => (
                  <option key={sign} value={sign} className="bg-surface text-text-primary">
                    {sign.charAt(0).toUpperCase() + sign.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="rising_sign" className="block text-sm font-medium text-text-primary mb-2">
                Rising Sign
              </label>
              <select
                id="rising_sign"
                name="rising_sign"
                required
                value={formData.rising_sign}
                onChange={handleChange}
                className="w-full px-3 py-2 glass border border-white/20 rounded-xl bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select Rising Sign</option>
                {zodiacSigns.map(sign => (
                  <option key={sign} value={sign} className="bg-surface text-text-primary">
                    {sign.charAt(0).toUpperCase() + sign.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-text-primary mb-2">
                Timezone
              </label>
              <Input
                id="timezone"
                name="timezone"
                type="text"
                required
                placeholder="America/New_York"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          {/* Location Search Button */}
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={handleLocationSearch}
              disabled={isLocationSearch}
              className="flex items-center space-x-2"
            >
              {isLocationSearch ? (
                <>
                  <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                  <span>Finding location...</span>
                </>
              ) : (
                <>
                  <Icon name="MapPin" size={18} />
                  <span>Find Coordinates</span>
                </>
              )}
            </Button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="glass bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={18} color="rgb(239 68 68)" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving birth data...</span>
              </div>
            ) : (
              'Complete Setup'
            )}
          </Button>
        </form>

        {/* Info */}
        <div className="glass bg-accent/10 border border-accent/20 rounded-xl p-4">
          <h3 className="text-sm font-medium text-accent mb-2">Why do we need this?</h3>
          <p className="text-xs text-text-secondary">
            Your birth data helps us calculate precise planetary positions and generate your unique cosmic musical profile. This information is securely stored and used only for astrological calculations.
          </p>
        </div>
      </div>
    </div>
  )
}

export default BirthDataSetup